import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const DIAGRAM_SVG_URL = `${process.env.PUBLIC_URL || ''}/images/Imagen4_desarmarColonial.svg`;

const PHRASE_IDS_TO_PATH = {
  // g60: '/Museo1',   // Colonialidad en las historias del mar
  // g70: '/Museo1',   // Hacer visible un objeto ausente
  // g80: '/Gispert2', // Cárcel, hospital, asilo, máquina de guerra...
  // g90: '/Museo2',   // ¿Cuál es el papel del museo?
  // g96: '/Gispert2', // "Raza", racismo y esclavitud
  g106: 'https://muse.jhu.edu/pub/6/article/718814/pdf', // Diagramas de barcos esclavistas
  g116: 'https://www.slavevoyages.org/', // ¿Cuántos barcos? ¿Cuántos viajes? ¿Cuántas personas?
};

const CENTER_GROUP_ID = 'g50';

const HOVER_CLASS = 'diagram-phrase-link-hover';

function setGroupTextContent(group, text) {
  const lines = (text || '').split('\n');
  const textEls = group.querySelectorAll('text');
  textEls.forEach((el, i) => { el.textContent = lines[i] || ''; });
}

const InstructivoDiagram = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const containerRef = useRef(null);
  const [svgContent, setSvgContent] = useState('');

  useEffect(() => {
    fetch(DIAGRAM_SVG_URL)
      .then((r) => r.text())
      .then((content) => {
        const withoutBg = content.replace(/fill:#0a3e83/g, 'fill:transparent');
        const noWhiteBg = withoutBg.replace(
          /id="path28"\s*style="fill:#ffffff[^"]*"/,
          'id="path28" style="fill:transparent;fill-opacity:0;stroke:none"'
        );
        setSvgContent(
          noWhiteBg.replace(
            /<svg/,
            '<svg class="instructivo-diagram-svg" style="max-width:100%;height:auto;display:block"'
          )
        );
      });
  }, []);

  useEffect(() => {
    if (!svgContent || !containerRef.current) return;
    const svg = containerRef.current.querySelector('svg');
    if (!svg) return;

    const centerEl = svg.querySelector(`#${CENTER_GROUP_ID}`);
    if (centerEl) setGroupTextContent(centerEl, t('instructivo.diagram.center'));

    Object.keys(PHRASE_IDS_TO_PATH).forEach((id) => {
      const el = svg.querySelector(`#${id}`);
      if (el) setGroupTextContent(el, t(`instructivo.diagram.${id}`));
    });
  }, [svgContent, i18n.language, t]);

  useEffect(() => {
    if (!svgContent || !containerRef.current) return;
    const svg = containerRef.current.querySelector('svg');
    if (!svg) return;

    const cleanupFns = [];
    Object.keys(PHRASE_IDS_TO_PATH).forEach((id) => {
      const el = svg.querySelector(`#${id}`);
      if (!el) return;
      const path = PHRASE_IDS_TO_PATH[id];
      el.style.cursor = 'pointer';

      const onClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        // Si el link empieza con http (como g106 y g116), ábrelo en otra pestaña
        if (path.startsWith('http')) {
          window.open(path, '_blank', 'noopener,noreferrer');
        } else {
          // Si es una ruta interna (como /Museo1), usa navigate
          navigate(path);
        }
      };
      const onMouseEnter = () => el.classList.add(HOVER_CLASS);
      const onMouseLeave = () => el.classList.remove(HOVER_CLASS);

      el.addEventListener('click', onClick);
      el.addEventListener('mouseenter', onMouseEnter);
      el.addEventListener('mouseleave', onMouseLeave);
      cleanupFns.push(() => {
        el.removeEventListener('click', onClick);
        el.removeEventListener('mouseenter', onMouseEnter);
        el.removeEventListener('mouseleave', onMouseLeave);
      });
    });

    return () => cleanupFns.forEach((fn) => fn());
  }, [svgContent, navigate]);

  if (!svgContent) return null;

  return (
    <figure style={{ marginTop: '2em', marginBottom: '1em' }} className="instructivo-diagram-figure">
      <div
        ref={containerRef}
        className="instructivo-diagram-wrap"
        dangerouslySetInnerHTML={{ __html: svgContent }}
      />
    </figure>
  );
};

export default InstructivoDiagram;
